/**
 * Catalog of (entity, action) pairs the Access Manager can assign.
 *
 * Entities are grouped by domain — the UI uses these groups as section
 * headings in the dual-list. Actions match the backend enum on
 * `user_permissions.action` and `role_permissions.action`.
 *
 * `full` is a shorthand — backend `getUserPermissions()` expands it into
 * every action. We still surface it as a selectable atom so users can
 * pick "full access for this entity" without ticking every box.
 */

export const ACTIONS = ['show', 'list', 'edit', 'create', 'delete', 'full', 'deploy'] as const;
export type Action = (typeof ACTIONS)[number];

export interface EntityGroup {
  label: string;
  entities: string[];
  /**
   * Apps this group is relevant to. Empty/undefined = always shown.
   * Used by `PermissionDualList` to scope the editor by current session app.
   */
  apps?: string[];
}

export const ENTITY_GROUPS: EntityGroup[] = [
  {
    label: 'Access Manager (Core)',
    entities: ['User', 'Role', 'UserPermission', 'RolePermission', 'ExternalAccessGrant'],
    // shown in every app
  },
  {
    // Notifications is a core feature: any user can receive + mark read without
    // a permission, but composing/sending requires Notification:create.
    label: 'Notifications',
    entities: ['Notification'],
  },
  {
    // Multi-tenant infrastructure used by every app — leaving `apps` empty
    // means the group is always visible regardless of the current scope.
    // Without this, an Access Manager working in xotski/xamplin/etc. couldn't
    // grant `CoCompany:show` / `CoBrand:show` and members would 403 on home.
    label: 'Companies & Brands',
    entities: ['CoCompany', 'CoBrand', 'CoCompanyAccess', 'CoBrandKit'],
  },
  {
    label: 'Xotski',
    entities: [
      'XkCompany',
      'XkStock',
      'XkEvent',
      'XkEventGroup',
      'XkStation',
      'XkEventStockStation',
      'XkDashboard',
    ],
    apps: ['xotski'],
  },
  {
    label: 'Xamplin',
    entities: ['XpProject', 'XpEvents', 'XpEventGroup', 'XpStation', 'XpReporter'],
    apps: ['xamplin'],
  },
  {
    label: 'Xurvey',
    entities: ['XySurvey', 'XyStep', 'XyComponent'],
    apps: ['xurvey'],
  },
  {
    label: 'DiD',
    entities: ['DidStation', 'DidStock', 'DidEvent', 'DidEventGroup'],
    apps: ['did'],
  },
  {
    label: 'Ecommerce',
    entities: ['EcPlan', 'EcInvoicePlan'],
    apps: ['console'],
  },
];

/**
 * Guess which app owns an entity from its name prefix. Used to filter the
 * "Other / Legacy" fallback so the dialog stays scoped to the current app.
 */
export function entityAppGuess(entity: string): string {
  if (entity.startsWith('Xk')) return 'xotski';
  if (entity.startsWith('Xp')) return 'xamplin';
  if (entity.startsWith('Xy')) return 'xurvey';
  if (entity.startsWith('Did')) return 'did';
  if (entity.startsWith('Co')) return 'console';
  if (entity.startsWith('Ec')) return 'console';
  return 'core'; // User, Role, UserPermission, RolePermission, ExternalAccessGrant, Notification
}

/**
 * True if an entity is relevant to a given app. `core` and `console` entities
 * (User/Role/CoCompany/CoBrand/...) match every app because they're shared
 * multi-tenant infrastructure used by all of them.
 */
export function entityMatchesApp(entity: string, app: string | null | undefined): boolean {
  if (!app) return true;
  const guess = entityAppGuess(entity);
  return guess === 'core' || guess === 'console' || guess === app;
}

/**
 * Flat list of every (entity, action) the catalog exposes.
 * When `app` is provided, only entities for that app + core are included.
 */
export function flatCatalog(app?: string | null): Array<{ entity: string; action: Action; group: string }> {
  const out: Array<{ entity: string; action: Action; group: string }> = [];
  for (const g of ENTITY_GROUPS) {
    if (app && g.apps && !g.apps.includes(app)) continue;
    for (const entity of g.entities) {
      for (const action of ACTIONS) {
        // Group by capability, not by backend module: the user is choosing
        // what someone can DO, not which service owns the row. ENTITY_GROUPS
        // still decides which entities this app shows at all.
        out.push({ entity, action, group: capabilityGroup(entity) });
      }
    }
  }
  return out;
}

export function permissionId(entity: string, action: string): string {
  return `${entity}::${action}`;
}

export function parsePermissionId(id: string): { entity: string; action: Action } {
  const [entity, action] = id.split('::');
  return { entity, action: action as Action };
}

// ── Human-facing capability model (tasks 6.2 + 6.4) ─────────────────────────
//
// The raw catalog is organised by backend module, and its rows are entity class
// names — `CoCompany`, `XkEventStockStation`, `RolePermission`. That is the
// schema talking. Permissions are the one screen where a non-technical admin
// has to make a security decision, so the words have to be theirs.
//
// These two maps sit on top of the catalog without changing it: the entity
// groups above still drive per-app filtering, while what the user reads comes
// from here.

/** The five buckets a capability can belong to, in display order. */
export const CAPABILITY_GROUPS = [
  'Brand & Journey',
  'Events & Stations',
  'Live',
  'Reports & Leads',
  'Team & Billing',
] as const;
export type CapabilityGroup = (typeof CAPABILITY_GROUPS)[number];

const ENTITY_CAPABILITY: Record<string, CapabilityGroup> = {
  // Brand & Journey — who the brand is and what the guest experiences.
  CoCompany: 'Brand & Journey',
  CoBrand: 'Brand & Journey',
  CoBrandKit: 'Brand & Journey',
  CoCompanyAccess: 'Brand & Journey',
  XkCompany: 'Brand & Journey',
  XySurvey: 'Brand & Journey',
  XyStep: 'Brand & Journey',
  XyComponent: 'Brand & Journey',

  // Events & Stations — the physical plan and what it carries.
  XkEvent: 'Events & Stations',
  XkEventGroup: 'Events & Stations',
  XkStation: 'Events & Stations',
  XkStock: 'Events & Stations',
  XkEventStockStation: 'Events & Stations',
  XpProject: 'Events & Stations',
  XpEvents: 'Events & Stations',
  XpEventGroup: 'Events & Stations',
  XpStation: 'Events & Stations',
  XpReporter: 'Events & Stations',
  DidEvent: 'Events & Stations',
  DidEventGroup: 'Events & Stations',
  DidStation: 'Events & Stations',
  DidStock: 'Events & Stations',

  // Live — what happens while an event is running.
  Notification: 'Live',

  // Reports & Leads.
  XkDashboard: 'Reports & Leads',

  // Team & Billing.
  User: 'Team & Billing',
  Role: 'Team & Billing',
  UserPermission: 'Team & Billing',
  RolePermission: 'Team & Billing',
  ExternalAccessGrant: 'Team & Billing',
  EcPlan: 'Team & Billing',
  EcInvoicePlan: 'Team & Billing',
};

/**
 * What a capability is called in front of a human. Never show the entity class
 * name: it is meaningless to a brand manager and, per the product vocabulary,
 * a database word leaking into the UI.
 */
const ENTITY_LABELS: Record<string, string> = {
  CoCompany: 'Brands',
  CoBrand: 'Brands',
  CoBrandKit: 'Brand kit',
  CoCompanyAccess: 'Brand access',
  XkCompany: 'Brands',
  XySurvey: 'Guest journeys',
  XyStep: 'Journey steps',
  XyComponent: 'Journey questions',
  XkEvent: 'Events',
  XkEventGroup: 'Projects',
  XkStation: 'Stations',
  XkStock: 'Merch',
  XkEventStockStation: 'Merch at stations',
  XkDashboard: 'Reports',
  XpProject: 'Projects',
  XpEvents: 'Events',
  XpEventGroup: 'Event groups',
  XpStation: 'Stations',
  XpReporter: 'Reporters',
  DidEvent: 'Events',
  DidEventGroup: 'Event groups',
  DidStation: 'Stations',
  DidStock: 'Merch',
  Notification: 'Notifications',
  User: 'Team members',
  Role: 'Roles',
  UserPermission: 'Member permissions',
  RolePermission: 'Role permissions',
  ExternalAccessGrant: 'External access',
  EcPlan: 'Plans',
  EcInvoicePlan: 'Invoices',
};

/** Human label for an entity; falls back to the raw name so a newly added
 *  entity is visibly unmapped rather than silently hidden. */
export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? entity;
}

/** Which of the five groups a capability belongs to. */
export function capabilityGroup(entity: string): string {
  return ENTITY_CAPABILITY[entity] ?? 'Other';
}
